// src/prototype/components/VerdictPill.tsx
// Executive verdict pill + the tinted decision banner that leads the Decision Brief.
// Colors come from the verdict tokens; the banner derives its border/background from
// the same token at alpha, so the two can never drift apart.
import React from 'react';
import { CheckCircle2, AlertTriangle, PauseCircle } from 'lucide-react';
import { VERDICTS, VERDICT_BANNER_ALPHA, type VerdictKey } from '../theme/decisionTokens';

const VERDICT_ICON: Record<VerdictKey, React.ElementType> = {
  green: CheckCircle2,
  cond: AlertTriangle,
  hold: PauseCircle,
};

export interface VerdictPillProps {
  verdict: VerdictKey;
  /** "sm" = short label for rows/cells; "lg" = full label for the decision banner. */
  size?: 'sm' | 'lg';
}

export const VerdictPill: React.FC<VerdictPillProps> = ({ verdict, size = 'sm' }) => {
  const token = VERDICTS[verdict];
  const Icon = VERDICT_ICON[verdict];
  const isLarge = size === 'lg';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-bold ${isLarge ? 'px-4 py-1.5 text-sm' : 'px-2.5 py-0.5 text-xs'}`}
      style={{ background: token.bg, color: token.fg }}
    >
      <Icon className={isLarge ? 'w-4 h-4' : 'w-3 h-3'} />
      {isLarge ? token.label : token.short}
    </span>
  );
};

export interface ExecutiveDecisionBannerProps {
  verdict: VerdictKey;
  /** 1–3 sentence decision rationale. */
  children: React.ReactNode;
}

export const ExecutiveDecisionBanner: React.FC<ExecutiveDecisionBannerProps> = ({ verdict, children }) => {
  const token = VERDICTS[verdict];
  const Icon = VERDICT_ICON[verdict];
  return (
    <div
      className="rounded-xl border p-4 flex items-start gap-3"
      style={{
        borderColor: token.dot + VERDICT_BANNER_ALPHA.border,
        background: token.bg + VERDICT_BANNER_ALPHA.background,
      }}
    >
      <Icon className="w-5 h-5 mt-0.5 shrink-0" style={{ color: token.dot }} />
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <VerdictPill verdict={verdict} size="lg" />
          <span className="text-xs font-semibold text-[#18365E] uppercase tracking-wide">Executive Decision</span>
        </div>
        <p className="text-sm text-[#4b5563] leading-relaxed mt-2">{children}</p>
      </div>
    </div>
  );
};

export default VerdictPill;
